import {
	btn,
	Button,
	column,
	comp,
	Component,
	cookies,
	DateTime,
	DefaultEntity,
	fieldset,
	Format,
	h3,
	h4,
	p,
	root,
	router,
	Store,
	table,
	tbar,
	Window
} from "@intermesh/goui";
import {CurrentSection} from "./services/CurrentSection.js";
import {CurrentCourse} from "./services/CurrentCourse.js";
import {CurrentParticipant} from "./services/CurrentParticipant.js";
import {t} from "./util/Translate.js";
import {CountdownPanel, countdownpanel} from "./components/CountdownPanel.js";
import {CurrentUser} from "./services/CurrentUser.js";
import {ServerApi} from "./util/ServerApi.js";
import {IfwFormat} from "./util/IfwFormat.js";

interface Block extends DefaultEntity {
	id: string,
	name: string,
	description: string,
	succeeded: boolean,
	expiresAt?: string
}

export class CourseStatus extends Component {
	private currentCourse;
	private readonly currentParticipant;
	private currentSection;
	private readonly blocksCard: Component;
	private readonly blocksList: Component;
	private backToStartBtn: Button;
	private readonly timerCmp: CountdownPanel;

	constructor() {
		super("section");
		this.itemId = "course-status";
		this.id = "course-status";
		this.cls = "card";

		this.currentCourse = CurrentCourse.get();
		this.currentParticipant = CurrentParticipant.get();
		this.currentSection = CurrentSection.get();

		const mql = window.matchMedia("(max-width: 600px)");

		const bStore = this.createStore(),
			pc = JSON.parse(localStorage.getItem("portalConfig")!);
		let allBlocksSucceeded: boolean = true, showCountDown: boolean = false;
		for (const item of bStore.getArray()) {
			allBlocksSucceeded = allBlocksSucceeded && (item.succeeded || item.exempt);
		}
		// Participants on-site with all blocks succeeded should get a timeout as well. *facepalm*
		if (allBlocksSucceeded && cookies.get("sectionAuth")) {
			showCountDown = true;
		}
		const showBackBtn = !allBlocksSucceeded || !cookies.get("sectionAuth");

		this.blocksList = comp({
				cls: "vbox",
				flex: 1,
			},
			table({
				store: bStore,
				headers: false,
				fitParent: true,
				columns: [
					column({
						id: "icon",
						sortable: false,
						resizable: false,
						width: 50,
						renderer: (value, record, _td, _table1, _storeIndex) => {
							const icon = record.exempt ? "layers_clear" : "layers";
							return btn({
								cls: (record.succeeded ? "icon success" : "goui icon"),
								icon: icon,
								title: record.name,
								handler: () => {
									return false;
								}
							})
						}
					}),

					column({
						id: "name",
						sortable: false,
						resizable: false,
						// width: 350,
						htmlEncode: false,
						renderer: (value, record, _td, _table1, _storeIndex) => {
							let thirdline: string;
							if (!record.succeeded) {
								thirdline = t("Not passed");
							} else {
								const now = new DateTime(), d = new DateTime(record.expiresAt);
								if (now.compare(d) > 0) {
									thirdline = t("Expired at");
								} else {
									thirdline = t("Expires at");
								}
								thirdline += " " + d.format("d-m-Y H:i");
							}

							return `<h3>${Format.escapeHTML(value)}</h3><h4>${Format.escapeHTML(record.description)}</h4><h5>${thirdline}</h5>`;

						}
					}),
					column({
						sortable: false,
						resizable: false,
						align: "right",
						id: "succeeded",
						renderer: (value, record, _td, _table1, _storeIndex) => {
							if (value) {
								return btn({
									cls: "filled primary",
									icon: "repeat",
									hidden: !allBlocksSucceeded || !pc.mayRepeatCourse,
									text: t("Repeat"),
									handler: () => {
										router.goto("course/" + this.currentCourse.id + "/" + record.id);

									}
								})
							}

							return comp({
								cls: "course-status-duration",
								html: '<i class="icon" style="display:inline;vertical-align: middle;">timer</i>&nbsp;' + IfwFormat.ifwDuration(record.estimatedDuration)
							})
						}
					})
				]
			})
		);

		this.blocksCard = comp({},
			fieldset({},
				comp({cls: "ifw-title-cnt"},
					h3({text: this.currentCourse.name, cls: "ifw-title"}),
					h4({text: this.currentCourse.description || "", cls: "ifw-subtitle"}),
				),
				p({
					hidden: allBlocksSucceeded,
					text: t("The course exists of the following parts. Succeeded parts are marked. Continue until all parts are succeeded.")
				}),
				p({
					hidden: !allBlocksSucceeded,
					text: t("Congratulations {firstName}! You've completed this course. You can print or e-mail the certificate.",
						"core", "core", {
							firstName: this.currentParticipant.firstName
						})
				}),
				comp({cls: "hbox gap"},
					this.blocksList,
					comp({cls: "vbox"}, comp({
							tagName: "img", attr: {
								src: "resources/completed-stamp.png",
								width: "160"
							}, hidden: !allBlocksSucceeded || mql.matches
						})
					)
				),
				tbar({
						flex: 1,
						style: {
							marginTop: "12px",
							justifyContent: "center"
						}
					},
					btn({
						cls: "accent filled",
						html: t("Print certificate"),
						hidden: !allBlocksSucceeded || !this.currentSection.frontendEnablePrint,
						handler: (_btn) => {
							window.open(ServerApi.url(this.getCertificateUrl(), {}, true));
						}
					}),

					btn({
						cls: "primary filled",
						html: t("E-mail certificate"),
						hidden: !allBlocksSucceeded || !this.currentSection.frontendEnableEmail,
						handler: async (_btn) => {
							const p = this.currentParticipant;
							const emailAddr = await Window.prompt(t("E-mail certificate"), t("Enter e-mail"), p.email, t("E-mail certificate"));
							if (emailAddr === undefined) {
								return; // Nothing to see here. Move along
							}
							this.emailCertificate(emailAddr);
						}
					}),
				)
			)
		);


		this.timerCmp = countdownpanel({timeoutSeconds: 60})

		this.items.add(tbar({cls: "goui-toolbar"},
				"->",
				btn({
					icon: "exit_to_app", title: t("Logout"), handler: async () => {
						await CurrentUser.confirmLogout();
					}
				}),
			),
			this.blocksCard,
			tbar({cls: "ifw-bbar ifw-center"},
				this.backToStartBtn = btn({
					hidden: !showBackBtn,
					html: t("Back to overview"),
					handler: () => {
						sessionStorage.removeItem(CurrentCourse.storageItem);
						if (!cookies.get("sectionAuth")) {
							sessionStorage.removeItem(CurrentSection.storageItem);
						}
						router.goto("profile");
					}
				}),
				"->",
				btn({
					cls: "primary filled",
					text: t("Continue"),
					hidden: allBlocksSucceeded,
					route: "course/" + this.currentCourse.id + "/"
				})
			),
		);
		const elCmp = root.findChild('el-container');

		this.on('render', async () => {
			// As per https://github.com/instructiefilm/elearning/issues/826#issuecomment-1983392390, if the timer is
			// displayed, hide the back to start button. This is way clearer than the old 'logic' in the Angular version.
			if (allBlocksSucceeded) {
				// Prevent reusing current invitation token!
				sessionStorage.removeItem("invitationToken");
				if (showCountDown && !this.timerCmp.rendered) {
					elCmp!.items.add(this.timerCmp);
					this.timerCmp.show();
					this.timerCmp.startCountdown();
					this.backToStartBtn.hide();
				} else if (cookies.get("sectionAuth") && !this.timerCmp.rendered && (sessionStorage.getItem("justFinishedCourse"))) {
					this.backToStartBtn.hide();
					elCmp!.items.add(this.timerCmp);
					this.timerCmp.show();
					this.timerCmp.startCountdown();
				}

				// #1077 expire 'old' course licenses
				const courseId = this.currentCourse.id, p = {
					participantId: this.currentParticipant.id,
					courseId: courseId,
					sectionId: this.currentSection.id
				}
				const route = ServerApi.url(CurrentCourse.baseRoute + "/" + courseId + "/expire"),
					expireStatus = await ServerApi.call(route, p, "POST");

				if (expireStatus.success && expireStatus.numExpired > 0) {
					sessionStorage.removeItem(CurrentParticipant.storageItem);
				}

				if (this.currentParticipant.email && this.currentSection.autoSendCertificate && sessionStorage.getItem("justFinishedCourse")) {
					sessionStorage.removeItem("justFinishedCourse");


					const portalConfig = JSON.parse(localStorage.getItem("portalConfig")!);

					if (portalConfig.mayUseEva) {
						// Wait for email to be sent, it will not finish otherwise because of the redirect
						this.emailCertificate(this.currentParticipant.email).then(() => {
							let evaPortal = sessionStorage.getItem("evaPortal");

							if (evaPortal) {
								let adhoc = sessionStorage.getItem("adhoc");

								if (adhoc) {
									let evaId = sessionStorage.getItem("evaId");
									if (evaId) {
										ServerApi.call('eva/signInToEva/' + evaId, {}, "POST")
									}
								}

								this.mask();

								setTimeout(() => {
									window.location.href = CurrentUser.evaPortalUrl() ?? "/";
									this.unmask();
								}, 1000);
							}
						});
					} else {
						void this.emailCertificate(this.currentParticipant.email);
					}
				}
			}
		})
	}

	createStore(): Store {
		const records: Block[] = [], now = new DateTime(),
			bStore = new Store();
		for (const b of this.currentCourse.blocks) {
			let obj = {
				id: b.block.id,
				name: b.block.name,
				description: b.block.description,
				estimatedDuration: b.block.estimatedDuration,
				succeeded: false,
				exempt: false
			} as Block;
			if (this.currentParticipant.attempts) {
				const attempts = this.currentParticipant.attempts.filter(a => a.blockId === b.block.id);
				for (const a of attempts) {
					const expiryDate = new DateTime(a.expiresAt);
					if (!obj.exempt) {
						obj.exempt = a.exempt;
					}
					if (a.succeeded && expiryDate.compare(now) > 0) {
						obj.succeeded = true;
						obj.expiresAt = a.expiresAt;
					}
				}
			}
			records.push(obj);
		}
		bStore.loadData(records);

		return bStore;
	}

	async emailCertificate(emailAddr: string) {
		const p = CurrentParticipant.get(); // In case the email was updated from 'E-mali certificate' button
		let url = "elearning/frontend/certificates/";
		if (this.hasCourseBasedCertificate()) {
			url += this.currentSection.id + "/" + this.currentCourse.id + "/" + p.id + "/emailcctoparticipant";
		} else {
			url += this.currentSection.id + '/' + p.id + '/emailtoparticipant';
		}
		url = ServerApi.url(url);
		sessionStorage.removeItem("justFinishedCourse");
		const mailResponse = await ServerApi.call(url, {
			subject: 'Your certificate',
			body: 'Your certificate is attached as PDF',
			email: emailAddr
		}, "POST");
		if (mailResponse.success) {
			await Window.alert(t("The certificate has been sent to {email}").replace("{email}", emailAddr), t("E-mail sent"));
		}
	}

	private hasCourseBasedCertificate(): boolean {
		for (const cl of this.currentCourse.languages) {
			if (cl.languageId === this.currentParticipant.languageId && cl.templateId !== null) {
				return true;
			}
			if (cl.languageId === 2 && cl.templateId !== null) {
				return true;
			}
		}
		return false;
	}

	private getCertificateUrl(): string {
		const p = CurrentParticipant.get(), c = CurrentCourse.get(), s = CurrentSection.get();
		let r = "elearning/frontend/certificates/";
		if (this.hasCourseBasedCertificate()) {
			r += c.id + "/" + p.id + "/cc"
		} else {
			r += s.id + "/" + p.id;
		}
		return r;
	}
}